/**
 * LINKURIOUS CONFIDENTIAL
 * Copyright Linkurious SAS 2012 - 2018
 *
 * Created by maximeallex on 2018-07-17.
 */


'use strict';

import { Injectable, Type } from '@angular/core';
import { AbstractModalComponent } from './abstractmodal';
import { SafeHtml } from '@angular/platform-browser';
import { Subject } from 'rxjs/Subject';
import { Tools } from 'linkurious-shared';
import { ActionConfiguration, ModalAction } from './modalfooter/modalaction';

export interface AbstractModalConfiguration {
  implicitDismiss?: boolean;
  size?: 'small'|'large'|'fullscreen';
  height?: number,
  resolve?: {[key: string]: Promise<any>|any};
  onValidate?: ActionConfiguration;
  onDismiss?: ActionConfiguration;
}

export interface ModalConfiguration extends AbstractModalConfiguration {
  content: Type<AbstractModalComponent>;
}

export interface ConfirmConfiguration extends AbstractModalConfiguration {
  content: SafeHtml;
}

export interface ModalEvent {
  content: Type<AbstractModalComponent>|SafeHtml;
  implicitDismiss?: boolean;
  size?: 'small'|'large'|'fullscreen';
  height?: number,
  resolve?: {[key: string]: any};
  onValidate?: ModalAction;
  onDismiss?: ModalAction;
}

export interface ConfirmEvent extends ModalEvent {
  title: SafeHtml;
}

@Injectable()
export class ModalService {

  public event$: Subject<ModalEvent|LoadingModalEvent|ConfirmEvent|CloseEvent> = new Subject();

  constructor() {}

  /**
   * Open a modal
   */
  public open(config: {
    content: Type<AbstractModalComponent>;
    implicitDismiss?: boolean;
    size?: 'small'|'large'|'fullscreen';
    height?: number;
    resolve?: {[key: string]: Promise<any>|any};
    onValidate?: ActionConfiguration;
    onDismiss?: ActionConfiguration;
  }): void {
    this.event$.next(new LoadingModalEvent());
    this.generateEvent(config)
      .then(modalEvent => {
        this.event$.next(modalEvent);
      });
  }

  /**
   * Open a confirm
   */
  public confirm(config: {
    title: SafeHtml;
    content: SafeHtml;
    implicitDismiss?: boolean;
    onValidate?: ActionConfiguration;
    onDismiss?: ActionConfiguration
  }): void {
    this.generateEvent(config)
      .then((modalEvent) => {
        this.event$.next({
          ...modalEvent,
          title: config.title,
          implicitDismiss: (Tools.isDefined(config.implicitDismiss)) ? config.implicitDismiss : true,
          size: 'small',
          onDismiss: (config.onDismiss) ? new ModalAction(config.onDismiss, 'dismiss') : new ModalAction({
            label: 'Close',
            onClick: () => {
              this.close();
            }
          }, 'dismiss')
        });
      });
  }

  /**
   * Generate the event object send to the modal component
   */
  generateEvent(config: ModalConfiguration|ConfirmConfiguration): Promise<ModalEvent> {
    let modalEvent = {
      ...config,
      implicitDismiss: (Tools.isDefined(config.implicitDismiss)) ? config.implicitDismiss : true,
      size: config.size || 'small',
      content: config.content,
      height: config.height,
      onValidate: ( config.onValidate ) ? new ModalAction(config.onValidate, 'validate') : undefined,
      onDismiss: ( config.onDismiss ) ? new ModalAction(config.onDismiss, 'dismiss') : undefined
    };
    if ( config.resolve ) {
      let promises = [];
      Object.keys(config.resolve).forEach(key => {
        promises.push(config.resolve[key]);
      });

      return Promise.all(promises).then(results => {
        Object.keys(config.resolve).forEach(( key, index ) => {
          modalEvent.resolve[key] = results[index];
        });
        return modalEvent;
      }).catch((e) => {
        return Promise.reject(e);
      });
    } else {
      return Promise.resolve({...modalEvent, resolve: {}});
    }
  }

  /**
   * Close a modal or confirm
   */
  public close(): void {
    this.event$.next(new CloseEvent());
  }
}

export class CloseEvent {}

export class LoadingModalEvent {}
